import { FC, memo, useEffect, useMemo, useState } from 'react';
import { Col, Row, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  AppointmentStatistics,
  fetchAppointmentStatistics,
  sumGender
} from 'services/api/statistics';

import { Loading } from 'components/ui';
import AgeGroup from './AgeGroup';
import AppointmentsGender from './AppointmentsGender';
import AppointmentsMonthly from './AppointmentsMonthly';
import PageView from './PageView';

const { Title } = Typography;

const StyledCharts = styled.div`
  margin-top: 40px;
  .chart-item-card {
    position: relative;
    &::after {
      content: '';
      position: absolute;
      z-index: 10;
      left: 24px;
      bottom: 24px;
      height: 20px;
      width: 100px;
      background-color: #ffffff;
    }
  }
`;

const Charts: FC = () => {
  const { t } = useTranslation();
  const [result, setResult] = useState<AppointmentStatistics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAppointmentStatistics()
      .then(setResult)
      .catch(() => setResult(null))
      .finally(() => setLoading(false));
  }, []);

  const genderCounts = useMemo(
    () => sumGender(result?.gender),
    [result?.gender]
  );

  if (loading || !result) {
    return <Loading />;
  }

  return (
    <StyledCharts>
      <Title level={4}>{t('dashboard.charts')}</Title>
      <Row gutter={[32, 32]} align="stretch" wrap>
        <Col xs={24} lg={12} className="chart-item-card">
          <AppointmentsMonthly
            total={result?.appointmentsCount || 0}
            data={
              result.dateGroup?.map(item => ({
                date: item.month,
                value: +item.count
              })) || []
            }
          />
        </Col>
        <Col xs={24} lg={12} className="chart-item-card">
          <AgeGroup
            total={result?.appointmentsCount || 0}
            data={
              result.ageGroup?.map(item => ({
                value: +item.count,
                category: item.age
              })) || []
            }
          />
        </Col>
        <Col xs={24} lg={12} className="chart-item-card">
          <AppointmentsGender
            total={result?.appointmentsCount || 0}
            data={[
              {
                category: t('dashboard.male'),
                value: genderCounts.male
              },
              {
                category: t('dashboard.female'),
                value: genderCounts.female
              }
            ]}
          />
        </Col>
        <Col xs={24} lg={12} className="chart-item-card">
          <PageView />
        </Col>
      </Row>
    </StyledCharts>
  );
};

export default memo(Charts);
